import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetApp from "@mui/icons-material/GetApp";

import KOGImage from "../kogImage/kogImage";
import KOGPreviewFile from "../kogPreviewFile/kogPreviewFile";
import RenderTooltipMessage from "../renderTooltipMessage/renderTooltipMessage";
import { formatFilename } from "../kogTextEllipsis/utilsTextEllipsis";
import FileComment from "./fileComment";

import { getTranslateByKey } from "@/main/services/utils";

import * as S from "./styles";

export interface IAttachmentCardIds {
	card?: string;
	preview?: string;
	image?: string;
	deleteButton: string;
	downloadButton: string;
	comment: string;
}

interface IAttachmentCardProps {
	ids: IAttachmentCardIds;
	fileName: string;
	fileNameTooltip?: string;
	comment?: string;
	isImage: boolean;
	imageSource?: string;
	fileExtension?: string;
	canDelete: boolean;
	canDownload: boolean;
	onDelete: () => void;
	onDownload: () => void;
	onPreview?: () => void;
}

function stopEvent(event: React.MouseEvent) {
	event.preventDefault();
	event.stopPropagation();
}

function AttachmentCard({
	ids,
	fileName,
	fileNameTooltip,
	comment,
	isImage,
	imageSource,
	fileExtension,
	canDelete,
	canDownload,
	onDelete,
	onDownload,
	onPreview,
}: IAttachmentCardProps) {
	const classes = S.useStyles();
	const fileNameParts = formatFilename(fileName);

	return (
		<S.BoxImage id={ids.card}>
			<S.PreviewFrame $isImage={isImage}>
				<S.PreviewContent id={ids.preview} $isClickable={onPreview !== undefined} onClick={onPreview}>
					{isImage ? (
						<KOGImage id={ids.image} src={imageSource} alt={fileName} />
					) : (
						<KOGPreviewFile fileExtension={fileExtension} showPreview={false} />
					)}
					{canDelete && (
						<S.ButtonDeletePreview
							id={ids.deleteButton}
							$isOverCorner={!isImage}
							onClick={(event) => {
								stopEvent(event);
								onDelete();
							}}
						>
							<CloseIcon fontSize="small" />
						</S.ButtonDeletePreview>
					)}
				</S.PreviewContent>
			</S.PreviewFrame>

			<S.FileInfo>
				<S.FileNameRow>
					<S.FileNameArea>
						<RenderTooltipMessage placement="top" tooltipMessage={fileNameTooltip ?? fileName} isWidthChildren>
							<S.LabelFileName>
								<S.FileNameBase>{fileNameParts.name}</S.FileNameBase>
								<S.FileNameExtension>{fileNameParts.extension}</S.FileNameExtension>
							</S.LabelFileName>
						</RenderTooltipMessage>
					</S.FileNameArea>

					{canDownload && (
						<S.DownloadArea>
							<Tooltip title={getTranslateByKey("information.downloadFile")}>
								<IconButton
									className={classes.buttonBaseDeletePreview}
									id={ids.downloadButton}
									onClick={(event) => {
										stopEvent(event);
										onDownload();
									}}
								>
									<GetApp fontSize="small" />
								</IconButton>
							</Tooltip>
						</S.DownloadArea>
					)}
				</S.FileNameRow>

				<FileComment id={ids.comment} comment={comment} />
			</S.FileInfo>
		</S.BoxImage>
	);
}

export default AttachmentCard;
